import React from "react";
import { Option } from "../../constants/enums";

const baseInput =
    "w-full bg-slate-950/50 border text-white py-3 rounded-2xl outline-none transition-all placeholder:text-slate-600 text-sm disabled:opacity-60 disabled:cursor-not-allowed [color-scheme:dark]";

const borderFor = (error?: string) =>
    error
        ? "border-red-500/50 focus:ring-2 focus:ring-red-500/30 focus:border-red-500"
        : "border-slate-800 focus:ring-2 focus:ring-[#8b5cf6]/40 focus:border-[#8b5cf6]";

interface FieldWrapperProps {
    label?: string;
    required?: boolean;
    error?: string;
    hint?: React.ReactNode;
    className?: string;
    children: React.ReactNode;
}

export function FieldWrapper({ label, required, error, hint, className = "", children }: FieldWrapperProps) {
    return (
        <div className={`space-y-1.5 ${className}`}>
            {label && (
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1">
                    {label}
                    {required && <span className="text-[#8b5cf6] ml-1">*</span>}
                </label>
            )}
            {children}
            {error ? (
                <p className="text-xs text-red-400 ml-1">{error}</p>
            ) : hint ? (
                <p className="text-xs text-slate-500 ml-1">{hint}</p>
            ) : null}
        </div>
    );
}

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string;
    hint?: React.ReactNode;
    icon?: React.ElementType;
    rightSlot?: React.ReactNode;
    wrapperClassName?: string;
}

export function Input({ label, error, hint, icon: Icon, rightSlot, required, wrapperClassName, className = "", ...rest }: InputProps) {
    return (
        <FieldWrapper label={label} required={required} error={error} hint={hint} className={wrapperClassName}>
            <div className="relative group">
                {Icon && (
                    <Icon
                        size={18}
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-[#8b5cf6] transition-colors pointer-events-none"
                    />
                )}
                <input
                    required={required}
                    className={`${baseInput} ${borderFor(error)} ${Icon ? "pl-12" : "pl-4"} ${rightSlot ? "pr-12" : "pr-4"} ${className}`}
                    {...rest}
                />
                {rightSlot && <div className="absolute right-3 top-1/2 -translate-y-1/2">{rightSlot}</div>}
            </div>
        </FieldWrapper>
    );
}

interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "children"> {
    label?: string;
    error?: string;
    hint?: React.ReactNode;
    options: Option[];
    placeholder?: string;
    wrapperClassName?: string;
}

export function Select({ label, error, hint, options, placeholder, required, wrapperClassName, className = "", ...rest }: SelectProps) {
    return (
        <FieldWrapper label={label} required={required} error={error} hint={hint} className={wrapperClassName}>
            <select
                required={required}
                className={`${baseInput} ${borderFor(error)} px-4 appearance-none bg-[url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")] bg-no-repeat bg-[right_1rem_center] pr-10 ${className}`}
                {...rest}
            >
                {placeholder !== undefined && (
                    <option value="" className="bg-slate-900">
                        {placeholder}
                    </option>
                )}
                {options.map((o) => (
                    <option key={o.value} value={o.value} className="bg-slate-900">
                        {o.label}
                    </option>
                ))}
            </select>
        </FieldWrapper>
    );
}

interface CheckboxProps {
    label: string;
    description?: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
}

export function Checkbox({ label, description, checked, onChange, disabled }: CheckboxProps) {
    return (
        <label
            className={`flex items-start gap-3 p-3.5 rounded-2xl border transition-colors cursor-pointer ${
                checked ? "border-[#8b5cf6]/40 bg-[#8b5cf6]/5" : "border-slate-800 bg-slate-950/30 hover:border-slate-700"
            } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
        >
            <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={(e) => onChange(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-600 bg-slate-900 accent-[#8b5cf6]"
            />
            <span>
                <span className="block text-sm font-semibold text-slate-200">{label}</span>
                {description && <span className="block text-xs text-slate-500 mt-0.5">{description}</span>}
            </span>
        </label>
    );
}

interface SegmentedProps<T extends string> {
    label?: string;
    value: T | "";
    options: Option<T>[];
    onChange: (value: T) => void;
    required?: boolean;
    error?: string;
    disabled?: boolean;
}

/** Pill toggle, e.g. HARDWARE | SOFTWARE */
export function Segmented<T extends string>({ label, value, options, onChange, required, error, disabled }: SegmentedProps<T>) {
    return (
        <FieldWrapper label={label} required={required} error={error}>
            <div className="grid gap-1 p-1 rounded-2xl bg-slate-950/50 border border-slate-800" style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}>
                {options.map((o) => (
                    <button
                        key={o.value}
                        type="button"
                        disabled={disabled}
                        onClick={() => onChange(o.value)}
                        className={`py-2.5 rounded-xl text-sm font-semibold transition-all disabled:cursor-not-allowed ${
                            value === o.value
                                ? "bg-[#8b5cf6] text-white shadow-lg shadow-[#8b5cf6]/20"
                                : "text-slate-400 hover:text-white hover:bg-slate-800/60"
                        }`}
                    >
                        {o.label}
                    </button>
                ))}
            </div>
        </FieldWrapper>
    );
}